import React, { useState } from 'react';
import { Row, Col, Table, Tooltip, Button, InputNumber, Modal, Avatar, Card, Badge, Icon } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import Colors from '../themes/Colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import NumberFormat from './NumberFormat';
import CustomInputCurrency from './customComponents/CustomInputCurrency/CustomInputCurrency';
import { contentDisplayIsSuspended } from '../services/display';
import { SeparateNumberFormat } from '../services/utils';

const AdViewComponent = ({
  currency,
  contentsDisplaysSelect,
  updateImpressions,
  updateImpressionsToMax,
  getSpotStatusTag,
  messageStatus,
  calculateImpressionsView,
  limitAddImpressions,
  calculateBudget,
  calculateDiffValue,
  updateBudget,
  getMinSpotsForRecord
}) => {
  const { t } = useTranslation();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState({ file: '', type: '' });

  const handlePreview = (file, type) => {
    setPreviewContent({ file, type });
    setPreviewVisible(true);
  };

  const handleCancelPreview = () => {
    setPreviewContent({ file: '', type: '' });
    setPreviewVisible(false);
  }

  const columns = [
    {
      title: t('Screen'),
      dataIndex: 'display_name',
      key: 'name',
      render: (text, record) => (
        <div>
          {getSpotStatusTag(record, t)}
          { messageStatus(record) }
          {text}
        </div>
      ),
    },
    {
      title: t('Version'),
      dataIndex: 'content_version_name',
      key: 'version',
      render: (text) => <Badge count={text} style={{ backgroundColor: Colors.primary }} />,
    },
    {
      title: t('Preview'),
      dataIndex: 'content.file_thumb',
      key: 'preview',
      render: (text, record) => {
        if (record.content.type && record.content.type.startsWith('video')) {
          return (
            <video
              width="64"
              height="64"
              autoPlay
              muted
              loop
              onClick={() => handlePreview(record.content.file, record.content.type)}
              style={{ cursor: 'pointer', objectFit: 'cover' }}
            >
              <source src={record.content.file} />
            </video>
          );
        }
        return (
          <Avatar
            shape="square"
            size={64}
            src={record.content.file_thumb}
            onClick={() => handlePreview(record.content.file, record.content.type)}
            style={{ cursor: 'pointer' }}
          />
        );
      },
    },
    {
      // title: (
      //   <Tooltip placement="top" title={t('Transfer')}>
      //     <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
      //       <FontAwesomeIcon icon={faExchangeAlt} style={{color: Colors.primaryAccent}}/>
      //     </div>
      //   </Tooltip>
      // ),
      key: 'transfer',
      width: 50,
      render: (text, record) => (
        <Tooltip placement="top" title={t('All spots are forwarded to this version')}>
                <Button
                    shape='circle'
                    disabled={contentDisplayIsSuspended(record)}
                    className='buttonActionsGroup'
                    onClick={() => updateImpressionsToMax(record.relationId)}
                    style={{ marginRight: "10px" }}
                >
                    <FontAwesomeIcon icon={faExchangeAlt} style={{color: Colors.primaryAccent}}/>
                </Button>
        </Tooltip>
      ),
    },
    {
      title: t('Pending spots'),
      key: 'pending_spots',
      render: (text, record) => {
        const minSpots = getMinSpotsForRecord ? getMinSpotsForRecord(record) : 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip title={t('Shows the remaining promised spots')}>
              <InputNumber
                min={minSpots}
                max={limitAddImpressions(record)}
                type="text"
                formatter={SeparateNumberFormat}
                parser={value => Number(value.replace(/\./g, ''))}
                disabled={record.subsidized_price > 0}
                value={record.promised_shows_origin !== record.promised_shows_update ? record.promised_shows_update : calculateImpressionsView(record)}
                onChange={(value) => {
                  const newValue = Math.min(Math.max(value, minSpots), limitAddImpressions(record));
                  updateImpressions(newValue, record.relationId);
                }}
              />
            </Tooltip>
            {record.appliedDiscount && (
              <Tooltip 
                title={
                  <div>
                    <Trans 
                      i18nKey="volume discount applied info"
                      values={{ 
                        minSpots: SeparateNumberFormat(record.appliedDiscount.min_impressions || 0),
                      }}
                      components={{ strong: <strong /> }}
                    />
                    <div style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '8px' }}>
                      {t('zero spots warning')}
                    </div>
                  </div>
                }
              >
                <Icon type="exclamation-circle" style={{ color: Colors.red, fontSize: '16px' }} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: t('Subtracted spots'),
      key: 'remaining_spots',
      render: (text, record) => (
          <InputNumber
            type="text"
            formatter={SeparateNumberFormat}
            parser={value => Number(value.replace(/\./g, ''))}
            disabled value={calculateDiffValue("subtract", record)} 
          />
      ),
    },
    {
      title: t('Added spots'),
      key: 'added_spots',
      render: (text, record) => (
          <InputNumber
            type="text"
            formatter={SeparateNumberFormat}
            parser={value => Number(value.replace(/\./g, ''))}
            disabled value={calculateDiffValue("add", record)} 
          />
      ),
    },
    {
      title: t('Budget'),
      key: 'budget',
      width: 250,
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <CustomInputCurrency
            currency={currency}
            value={calculateBudget("no_origin", record)}
            onChange={(value) => updateBudget(value, record.relationId)}
            style={{ flex: 1 }}
            disabled={contentDisplayIsSuspended(record)}
          />
          { record.promised_shows_origin != record.promised_shows_update ?
            <Tooltip
              placement="topLeft"
              title={`${t('Initial budget')}: ${calculateBudget("origin", record)}`}
            >
              <Icon style={{ fontSize: "15px", marginLeft: "5px", color:"#faad14"}} type="info-circle" />
            </Tooltip>
          :null}
        </div>
      ),
    },
    {
      title: t('CPM/spot'),
      dataIndex: 'smart_campaign_cpm',
      key: 'smart_campaign_cpm',
      render: (cpm) => (
        <Tooltip title={`${t('Cost per spot')}: $${cpm/1000}`}>
          <span>
            <NumberFormat value={cpm} currency={currency} />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {contentsDisplaysSelect.map(collection => {
        return(
          <Card key={collection.key} title={collection.key} headStyle={{ fontWeight: "bolder" }}>
            <Table
              columns={columns}
              dataSource={collection.customContents}
              rowKey="relationId"
              pagination={false}
            />
          </Card>
        )
      })}
      <Modal visible={previewVisible} footer={null} onCancel={handleCancelPreview}>
        {previewContent.type && previewContent.type.startsWith('video') ? (
          <video controls autoPlay width="100%">
            <source src={previewContent.file} />
          </video>
        ) : (
          <img width="100%" src={previewContent.file} />
        )}
      </Modal>
    </div>
  );
};

export default AdViewComponent;
